import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Scissors, Clock, MapPin, Instagram, Phone, Crown, ChevronRight, Calendar, Star } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const MAPS_QUERY = "Rua+Jo%C3%A3o+Luiz+de+Miranda+Coutinho,+524";
const MAPS_URL = (typeof navigator !== "undefined" && /iPad|iPhone|iPod/.test(navigator.userAgent))
  ? `https://maps.apple.com/?q=${MAPS_QUERY}`
  : `https://www.google.com/maps/search/?api=1&query=${MAPS_QUERY}`;

const LOGO = "https://customer-assets-m6fa6gv7.emergentagent.net/job_971b0a5e-2a58-4bd4-a5ef-b384a7e4c355/artifacts/qroeuqc4_D09061CF-773C-466E-9659-A1C4CF9EC193.png";
const HERO_IMG = "https://images.pexels.com/photos/3037244/pexels-photo-3037244.jpeg";
const CRAFT_IMG = "https://images.pexels.com/photos/6487890/pexels-photo-6487890.jpeg";
const NEON_IMG = "https://images.unsplash.com/photo-1557349800-5b9b168f3f53";

const SERVICES = [
  { num: "01", name: "Corte", price: "R$ 35", desc: "Degradê, social ou navalhado. Finalização na régua." },
  { num: "02", name: "Corte + Sobrancelha", price: "R$ 45", desc: "O corte completo com o olhar alinhado na navalha." },
  { num: "03", name: "Corte + Barba", price: "R$ 65", desc: "Toalha quente, navalha e óleo. O ritual completo." },
  { num: "04", name: "Corte + Barba + Sobrancelha", price: "R$ 70", desc: "O combo coroa. Sai da cadeira outra pessoa." },
  { num: "05", name: "Sobrancelha", price: "R$ 10", desc: "Alinhamento rápido na navalha. Só chegar." },
];

const DEFAULT_BARBERS = [
  { id: "lucas", name: "Lucas", role: "Barbeiro & Fundador", tag: "Especialista em degradê e navalha clássica" },
  { id: "izak", name: "Izak", role: "Barbeiro & Fundador", tag: "Precisão no corte e barba desenhada" },
];

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ y: 48, opacity: 0 }}
    whileInView={{ y: 0, opacity: 1 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.85, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const ChapterTag = ({ num, label }) => (
  <Reveal>
    <div className="flex items-center gap-4 mb-10" data-testid={`chapter-${num}`}>
      <span className="font-bebas text-gold text-2xl tracking-widest">{num}</span>
      <span className="h-px w-16 bg-gold/60" />
      <span className="font-manrope text-xs md:text-sm uppercase tracking-[0.35em] text-smoke">{label}</span>
    </div>
  </Reveal>
);

function Header() {
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
          <img src={LOGO} alt="Estúdio New Era" className="h-10 w-10 md:h-12 md:w-12 rounded-full object-cover ring-1 ring-gold/50" />
          <span className="font-bebas text-xl md:text-2xl tracking-widest text-cream">
            Estúdio <span className="text-gold">New Era</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm uppercase tracking-[0.2em] text-smoke">
          <a href="#menu" className="hover:text-gold transition-colors duration-300" data-testid="nav-services-link">O Menu</a>
          <a href="#mestres" className="hover:text-gold transition-colors duration-300" data-testid="nav-barbers-link">Os Mestres</a>
          <a href="#galeria" className="hover:text-gold transition-colors duration-300" data-testid="nav-gallery-link">Galeria</a>
          <a href="#casa" className="hover:text-gold transition-colors duration-300" data-testid="nav-contact-link">A Casa</a>
          <Link to="/consulta" className="hover:text-gold transition-colors duration-300" data-testid="nav-lookup-link">Consultar</Link>
        </nav>
        <Link
          to="/agendar"
          data-testid="header-booking-button"
          className="focus-gold bg-gold text-ink font-bebas tracking-widest text-lg px-5 md:px-7 py-2 hover:bg-golddark hover:-translate-y-0.5 transition-[background-color,transform] duration-300"
        >
          Agendar
        </Link>
      </div>
    </header>
  );
}

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: yBg }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="w-full h-[115%] object-cover object-center opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      </motion.div>

      <motion.img
        src={LOGO}
        alt="Logo Estúdio New Era"
        data-testid="hero-logo"
        style={{ y: yLogo }}
        initial={{ opacity: 0, scale: 0.85, rotate: -6 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1.4, delay: 0.9, ease: EASE }}
        className="hidden lg:block absolute right-[6%] top-1/2 -translate-y-1/2 w-[30vw] max-w-[420px] mix-blend-lighten animate-float-slow pointer-events-none select-none"
      />

      <motion.div style={{ opacity }} className="relative max-w-7xl mx-auto px-5 md:px-10 pt-28 pb-20 w-full">
        <MaskedLine delay={0.15}>
          <span className="font-manrope text-xs md:text-sm uppercase tracking-[0.45em] text-gold flex items-center gap-3">
            <Crown className="w-4 h-4" /> Barbearia Premium · Desde 2025
          </span>
        </MaskedLine>
        <h1 className="mt-6 leading-[0.85]">
          <MaskedLine delay={0.3} className="font-playfair italic text-gold text-4xl md:text-6xl">
            Estúdio
          </MaskedLine>
          <MaskedLine delay={0.45} className="font-bebas uppercase tracking-tighter text-cream text-[19vw] md:text-[13vw] lg:text-[10rem]">
            New Era
          </MaskedLine>
        </h1>
        <MaskedLine delay={0.65} className="mt-6 max-w-xl">
          <p className="text-sm md:text-base text-smoke leading-relaxed">
            Navalha afiada, cadeira sua. Corte, barba e sobrancelha com precisão de quem vive o ofício.
            Escolha o serviço, o barbeiro e o horário — direto aqui no site.
          </p>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/agendar"
            data-testid="hero-booking-cta"
            className="focus-gold group inline-flex items-center gap-3 bg-gold text-ink font-bebas text-xl md:text-2xl tracking-widest px-8 md:px-10 py-3 md:py-4 hover:bg-golddark hover:-translate-y-1 transition-[background-color,transform] duration-300"
          >
            Agendar horário
            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
          <a
            href="#menu"
            data-testid="hero-menu-cta"
            className="focus-gold font-bebas text-xl md:text-2xl tracking-widest px-8 py-3 md:py-4 border border-white/20 text-cream hover:border-gold hover:text-gold transition-colors duration-300"
          >
            Ver o menu
          </a>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-14 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-smoke"
        >
          <span className="h-px w-10 bg-gold/70" />
          Rua João Luiz de Miranda Coutinho, 524
        </motion.div>
      </motion.div>
    </section>
  );
}

function Marquee() {
  const items = ["Estúdio New Era", "Navalha na mão", "Corte na régua", "Barba alinhada", "Desde 2025"];
  const Row = () => (
    <div className="flex shrink-0 items-center">
      {items.map((t, i) => (
        <span key={i} className="flex items-center">
          <span className="font-bebas uppercase text-6xl md:text-8xl tracking-tight text-outline px-6 whitespace-nowrap">{t}</span>
          <span className="text-gold text-3xl md:text-5xl px-2">•</span>
        </span>
      ))}
    </div>
  );
  return (
    <div className="border-y border-white/10 py-6 md:py-8 overflow-hidden select-none" data-testid="marquee-section" aria-hidden="true">
      <div className="flex w-max animate-marquee">
        <Row />
        <Row />
      </div>
    </div>
  );
}

function Services() {
  return (
    <section id="menu" className="relative max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-36" data-testid="services-section">
      <ChapterTag num="01" label="O Menu" />
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal>
            <h2 className="font-bebas uppercase tracking-tight text-cream text-5xl md:text-6xl leading-[0.9]">
              Preço na lata.<br /><span className="text-gold">Sem enrolação.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-sm md:text-base text-smoke leading-relaxed max-w-md">
              Cinco serviços, zero frescura. Você escolhe, a gente executa.
              Todos os serviços com agendamento online e confirmação na hora.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 hidden lg:block">
            <div className="relative w-72 aspect-[3/4] overflow-hidden border border-white/10">
              <img src={CRAFT_IMG} alt="Detalhe de corte de precisão" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
              <span className="absolute bottom-4 left-4 font-playfair italic text-gold text-xl">o ofício</span>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <ul>
            {SERVICES.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.07}>
                <li
                  data-testid={`service-item-${s.num}`}
                  className="group flex items-baseline justify-between gap-4 border-b border-white/10 py-6 md:py-7 hover:border-gold/50 transition-colors duration-300"
                >
                  <div className="flex items-baseline gap-5">
                    <span className="font-bebas text-gold/60 text-lg">{s.num}</span>
                    <div>
                      <h3 className="font-bebas uppercase tracking-wide text-2xl md:text-3xl text-cream group-hover:text-gold group-hover:translate-x-1 transition-[color,transform] duration-300">
                        {s.name}
                      </h3>
                      <p className="text-xs md:text-sm text-smoke mt-1">{s.desc}</p>
                    </div>
                  </div>
                  <span className="font-bebas text-2xl md:text-3xl text-gold whitespace-nowrap">{s.price}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal delay={0.3}>
            <Link
              to="/agendar"
              data-testid="services-booking-cta"
              className="focus-gold mt-10 inline-flex items-center gap-3 border border-gold text-gold font-bebas text-xl tracking-widest px-8 py-3 hover:bg-gold hover:text-ink transition-colors duration-300"
            >
              <Calendar className="w-5 h-5" /> Garantir meu horário
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Barbers() {
  const [barbers, setBarbers] = useState(DEFAULT_BARBERS);
  useEffect(() => {
    axios.get(`${API}/barbers`).then((r) => {
      if (r.data.length) setBarbers(r.data);
    }).catch(() => {});
  }, []);
  return (
    <section id="mestres" className="relative border-y border-white/10 bg-surface/40" data-testid="barbers-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-36">
        <ChapterTag num="02" label="Os Mestres" />
        <Reveal>
          <h2 className="font-bebas uppercase tracking-tight text-cream text-5xl md:text-6xl leading-[0.9] max-w-2xl">
            Duas cadeiras.<br /><span className="text-gold">Um padrão.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {barbers.map((b, i) => (
            <Reveal key={b.name} delay={i * 0.12}>
              <div
                data-testid={`barber-card-${b.name.toLowerCase()}`}
                className="group relative border border-white/10 bg-ink p-8 md:p-10 overflow-hidden hover:border-gold/60 hover:-translate-y-1 transition-[border-color,transform] duration-300"
              >
                {b.photo && (
                  <>
                    <img src={`${BACKEND}/api/barbers/${b.id}/photo`} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-60 group-hover:scale-105 transition-[opacity,transform] duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/25" />
                  </>
                )}
                <span className="absolute top-6 right-8 font-bebas text-7xl md:text-8xl text-outline-white opacity-30 select-none z-10">
                  0{i + 1}
                </span>
                <div className="relative z-10">
                <Scissors className="w-8 h-8 text-gold mb-6" />
                <h3 className="font-bebas uppercase tracking-wide text-4xl md:text-5xl text-cream">{b.name}</h3>
                <p className="font-playfair italic text-gold text-lg mt-1">{b.role}</p>
                <p className="text-sm text-smoke mt-4 max-w-xs">{b.tag}</p>
                <Link
                  to="/agendar"
                  data-testid={`barber-book-${b.name.toLowerCase()}`}
                  className="focus-gold mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold hover:gap-4 transition-[gap] duration-300"
                >
                  Agendar com {b.name} <ChevronRight className="w-4 h-4" />
                </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Reviews() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setItems(r.data)).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <section id="avaliacoes" className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-36" data-testid="reviews-section">
      <ChapterTag num="03" label="Quem corta, aprova" />
      <Reveal>
        <h2 className="font-bebas uppercase tracking-tight text-cream text-5xl md:text-6xl leading-[0.9]">
          Palavra de<br /><span className="text-gold">quem sentou na cadeira.</span>
        </h2>
      </Reveal>
      <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r, i) => (
          <Reveal key={r.id} delay={i * 0.07}>
            <div className="border border-white/10 bg-surface p-6 h-full flex flex-col" data-testid={`review-card-${r.id}`}>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "text-gold fill-gold" : "text-smoke/30"}`} />
                ))}
              </div>
              {r.comment && <p className="text-sm text-cream leading-relaxed mt-4 flex-1">&ldquo;{r.comment}&rdquo;</p>}
              <p className="text-xs uppercase tracking-[0.2em] text-smoke mt-4">{r.name} · {r.service_name}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function Gallery() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => setItems(r.data)).catch(() => {});
  }, []);
  if (!items.length) return null;
  return (
    <section id="galeria" className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-36" data-testid="gallery-section">
      <ChapterTag num="04" label="Galeria" />
      <Reveal>
        <h2 className="font-bebas uppercase tracking-tight text-cream text-5xl md:text-6xl leading-[0.9]">
          Trabalho que<br /><span className="text-gold">fala por si.</span>
        </h2>
      </Reveal>
      <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((it, i) => (
          <Reveal key={it.id} delay={i * 0.05}>
            <figure className="group relative aspect-[3/4] overflow-hidden border border-white/10" data-testid={`home-gallery-item-${it.id}`}>
              <img
                src={`${BACKEND}${it.url}`}
                alt={it.caption || "Corte feito no estúdio"}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              {it.caption && (
                <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink/90 to-transparent p-3 text-xs text-cream">
                  {it.caption}
                </figcaption>
              )}
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const DEFAULT_ROWS = [
  { icon: Clock, label: "Segunda", value: "14:00 — 19:30" },
  { icon: Clock, label: "Terça a Sábado", value: "09:00 — 12:00 · 14:00 — 19:30" },
  { icon: Clock, label: "Domingo", value: "Fechado" },
];

function Contact() {
  const [rows, setRows] = useState(DEFAULT_ROWS);
  useEffect(() => {
    axios.get(`${API}/hours`).then((r) => {
      const labels = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
      setRows(labels.map((label, i) => {
        const d = r.data[String(i)] || { closed: true, windows: [] };
        const value = d.closed || !d.windows.length
          ? "Fechado"
          : d.windows.map((w) => `${w[0]} — ${w[1]}`).join(" · ");
        return { icon: Clock, label, value };
      }));
    }).catch(() => {});
  }, []);
  return (
    <section id="casa" className="relative max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-36" data-testid="contact-section">
      <ChapterTag num="05" label="A Casa" />
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <Reveal>
            <h2 className="font-bebas uppercase tracking-tight text-cream text-5xl md:text-6xl leading-[0.9]">
              Chega junto.<br /><span className="text-gold">A cadeira é sua.</span>
            </h2>
          </Reveal>
          <div className="mt-12 space-y-0 border-t border-white/10">
            {rows.map((r, i) => (
              <Reveal key={r.label} delay={i * 0.08}>
                <div className="flex items-center justify-between gap-4 border-b border-white/10 py-5" data-testid={`hours-row-${i}`}>
                  <span className="flex items-center gap-3 text-sm uppercase tracking-[0.25em] text-smoke">
                    <r.icon className="w-4 h-4 text-gold" /> {r.label}
                  </span>
                  <span className={`font-bebas text-xl md:text-2xl tracking-wide ${r.value === "Fechado" ? "text-eraorange" : "text-cream"}`}>
                    {r.value}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25}>
            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              <a
                href={MAPS_URL}
                target="_blank" rel="noreferrer"
                data-testid="contact-address-link"
                className="focus-gold group border border-white/10 p-5 hover:border-gold/60 transition-colors duration-300"
              >
                <MapPin className="w-5 h-5 text-gold mb-3" />
                <p className="text-xs uppercase tracking-[0.2em] text-smoke mb-1">Endereço</p>
                <p className="text-sm text-cream leading-snug">Rua João Luiz de Miranda Coutinho, 524</p>
              </a>
              <a
                href="https://instagram.com/neweraestudio"
                target="_blank" rel="noreferrer"
                data-testid="contact-instagram-link"
                className="focus-gold group border border-white/10 p-5 hover:border-gold/60 transition-colors duration-300"
              >
                <Instagram className="w-5 h-5 text-gold mb-3" />
                <p className="text-xs uppercase tracking-[0.2em] text-smoke mb-1">Instagram</p>
                <p className="text-sm text-cream">@neweraestudio</p>
              </a>
              <a
                href="https://wa.me/5547997755448"
                target="_blank" rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="focus-gold group border border-white/10 p-5 hover:border-gold/60 transition-colors duration-300"
              >
                <Phone className="w-5 h-5 text-gold mb-3" />
                <p className="text-xs uppercase tracking-[0.2em] text-smoke mb-1">WhatsApp</p>
                <p className="text-sm text-cream">(47) 99775-5448</p>
              </a>
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="lg:col-span-5">
          <div className="relative h-full min-h-[320px] overflow-hidden border border-white/10">
            <img src={NEON_IMG} alt="Ambiente do estúdio" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="font-playfair italic text-gold text-2xl md:text-3xl">"Sai da cadeira outra pessoa."</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="relative border-t border-white/10 overflow-hidden" data-testid="final-cta-section">
      <img src={LOGO} alt="" className="absolute -right-24 -bottom-24 w-[480px] opacity-20 mix-blend-lighten pointer-events-none select-none" />
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-24 md:py-32">
        <Reveal>
          <p className="font-manrope text-xs uppercase tracking-[0.45em] text-gold mb-6">Sem fila. Sem espera.</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-bebas uppercase tracking-tighter text-cream text-6xl md:text-8xl leading-[0.85]">
            Sua nova era<br /><span className="text-outline">começa na cadeira</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <Link
            to="/agendar"
            data-testid="final-booking-cta"
            className="focus-gold group mt-12 inline-flex items-center gap-3 bg-gold text-ink font-bebas text-2xl md:text-3xl tracking-widest px-10 md:px-14 py-4 md:py-5 hover:bg-golddark hover:-translate-y-1 transition-[background-color,transform] duration-300"
          >
            Agendar agora
            <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/10 bg-black" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="Estúdio New Era" className="h-10 w-10 rounded-full object-cover ring-1 ring-gold/50" />
          <span className="font-bebas text-xl tracking-widest text-cream">Estúdio <span className="text-gold">New Era</span></span>
        </div>
        <div className="flex items-center gap-6">
          <Link to="/consulta" className="text-xs uppercase tracking-[0.3em] text-smoke hover:text-gold transition-colors" data-testid="footer-lookup-link">Consultar agendamento</Link>
          <p className="text-xs uppercase tracking-[0.3em] text-smoke">Desde 2025</p>
        </div>
        <a href="https://instagram.com/neweraestudio" target="_blank" rel="noreferrer" data-testid="footer-instagram-link" className="focus-gold text-gold hover:text-golddark transition-colors">
          <Instagram className="w-5 h-5" />
        </a>
      </div>
    </footer>
  );
}

export default function Home() {
  return (
    <div className="bg-ink min-h-screen" data-testid="home-page">
      <Header />
      <Hero />
      <Marquee />
      <Services />
      <Barbers />
      <Reviews />
      <Gallery />
      <Contact />
      <FinalCta />
      <Footer />
    </div>
  );
}
